import {IconButton} from '@wandb/components';
import React, {useCallback} from 'react';
import {useHistory} from 'react-router-dom';

import {type Ref as PanelBankSectionConfigRef} from '../../../state/views/panelBankSectionConfig/types';
import {isMobile} from '../../../util/panelbankFlow';
import {usePanelContext} from '../PanelContextProvider';
import {PanelPopupMode} from '../types';
import {useGetFullScreenPanelLink} from './FullScreenPanelLink';

export const PanelFullScreenButton = ({
  panelBankSectionConfigRef,
}: {
  panelBankSectionConfigRef: PanelBankSectionConfigRef | undefined;
}) => {
  return (
    <WorkspaceFullScreenPanelButton
      panelBankSectionConfigRef={panelBankSectionConfigRef}
    />
  );
};

const FullScreenButton = ({onClick}: {onClick: () => void}) => {
  return (
    <IconButton
      icon="full-screen-mode-expand"
      variant="ghost"
      aria-label="View full screen"
      data-dd-action-name="View full screen"
      size="sm"
      tooltip={{children: 'View full screen'}}
      onPress={e => {
        e.continuePropagation();
        onClick();
      }}
    />
  );
};

/**
 * Clicking the full screen button in a workspace should redirect to the full
 * screen panel page where it's overlayed on top of panel bank.
 */
const WorkspaceFullScreenPanelButton = ({
  panelBankSectionConfigRef,
}: {
  panelBankSectionConfigRef: PanelBankSectionConfigRef | undefined;
}) => {
  const {panel, trackPanelControls} = usePanelContext();
  const history = useHistory();
  const getLink = useGetFullScreenPanelLink(panel, panelBankSectionConfigRef);
  const onClick = useCallback(() => {
    window.scrollTo(0, 0);

    trackPanelControls({
      action: 'View full screen clicked',
    });
    history.push(getLink());
  }, [getLink, history, trackPanelControls]);

  return <FullScreenButton onClick={onClick} />;
};

/**
 * Report's full screen should still render the popup modal.
 * It has not been migrated to the same flow as in workspaces.
 */
export const ReportFullScreenPanelButton = () => {
  const {trackPanelControls} = usePanelContext();
  const {setPanelPopupMode} = usePanelContext();

  const onClick = useCallback(() => {
    setPanelPopupMode(PanelPopupMode.FULL_SCREEN);
    trackPanelControls({
      action: 'View full screen clicked',
    });
  }, [setPanelPopupMode, trackPanelControls]);

  if (isMobile()) {
    return null;
  }

  return <FullScreenButton onClick={onClick} />;
};
